import { signOut as amplifySignOut } from 'aws-amplify/auth';

import {
  APP_BASE_URI,
  AUTH_BASE_URL,
  AUTH_CLIENT_ID,
  AUTH_RESPONSE_TYPE,
  AUTH_SIGN_OUT_URL,
  OAUTH_SCOPES,
} from './constants';
import { setStorageValue } from 'src/common/util/useLocalStorage/setStorageValue';
import { currentOrgStorageKey } from 'src/features/auth/auth.slice';
import { clearActiveMembership } from 'src/features/notifications/serviceWorkerMessages';

// Fully sign out of the Cognito Hosted UI and land on the marketing site.
//
// Amplify v6's signOut clears the local tokens but then refuses to redirect to
// Cognito's /logout endpoint: getRedirectUrl rejects our redirectSignOut (the
// marketing site) as a different origin than the app and throws
// InvalidOriginException. We let it clear the tokens, swallow that throw, and
// navigate to /logout ourselves. Hitting /logout is what clears the Hosted-UI
// session cookie; without it the next sign-in silently re-authenticates off the
// cookie and the user never actually logs out (BAM-548).
export async function signOut() {
  await clearLocalSession();
  window.location.assign(logoutUrl({ logout_uri: String(AUTH_SIGN_OUT_URL) }));
}

// Sign out and come back to the login form. Cognito accepts a logout_uri only
// when the client has that URL registered as a sign-out URL, and answers one it
// does not recognize with /error?error=Required+parameters+missing, a page with
// no way back to the app. The authorize parameters send the browser on to the
// login form via the callback URL instead, which every client that can sign in
// already has registered.
export async function signOutAndReauthenticate() {
  await clearLocalSession();
  window.location.assign(
    logoutUrl({
      response_type: String(AUTH_RESPONSE_TYPE),
      scope: OAUTH_SCOPES.join(' '),
      redirect_uri: String(APP_BASE_URI),
    })
  );
}

function logoutUrl(params: Record<string, string>) {
  const query = new URLSearchParams({
    client_id: String(AUTH_CLIENT_ID),
    ...params,
  });
  return `${(AUTH_BASE_URL ?? '').replace(/\/$/, '')}/logout?${query}`;
}

async function clearLocalSession() {
  // Belt-and-suspenders: signIn already drops a stale org selection when the
  // org isn't in the profile's memberships, but clear it on sign-out too.
  // Best-effort — localStorage can throw (disabled/quota), and that must not
  // block the actual sign-out below.
  try {
    setStorageValue(currentOrgStorageKey, '');
  } catch {
    // ignore; signIn also clears this
  }

  // Stop the service worker from showing this user's pushes once signed out.
  // Best-effort for the same reason as above.
  try {
    await clearActiveMembership();
  } catch {
    // ignore; the next sign-in overwrites the active membership
  }

  try {
    await amplifySignOut();
  } catch (err) {
    // Expected: Amplify throws InvalidOriginException on our cross-origin
    // redirectSignOut, *after* clearing tokens. Log anything unexpected but
    // always continue — reaching /logout (cookie clear) is what logs the user
    // out, so we must not let an Amplify error abort the navigation.
    console.error(
      'Amplify signOut failed; continuing to Hosted UI logout',
      err
    );
  }
}
